import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useAuthStore } from './auth';
import type { UIRefType } from '../types';

// Re-export types from shared package
export type {
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationFull,
  GitProvider,
  GitIntegration,
  GitIntegrationConfig,
  Repository,
  RepositoryUpdate,
  BranchInfo,
  TagInfo,
  PullRequestInfo,
  PullRequestAuthor,
  CommitInfo,
  GitProviderCapabilities,
  PipelineInfo,
  PipelinesAtRefResponse,
  PipelineScheduleInfo,
} from '@kilnci/shared';

import type {
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationFull,
  GitIntegration,
  GitIntegrationConfig,
  Repository,
  RepositoryUpdate,
  BranchInfo,
  TagInfo,
  PullRequestInfo,
  GitProviderCapabilities,
  PipelinesAtRefResponse,
  PipelineScheduleInfo,
} from '@kilnci/shared';

// Extended type for API response that includes capabilities
export type GitIntegrationWithCapabilities = GitIntegration & {
  capabilities?: GitProviderCapabilities;
};

export interface SlackIntegration {
  id: string;
  repositoryId: string;
  defaultChannel: string | null;
  enabled: boolean;
  notifyOnStart: boolean;
  notifyOnSuccess: boolean;
  notifyOnFailure: boolean;
  notifyOnCancelled: boolean;
  // OAuth-specific fields
  workspaceId: string | null;
  workspaceName: string | null;
  channelId: string | null;
  source: 'manual' | 'oauth';
  createdAt: string;
  updatedAt: string;
}

export interface SlackIntegrationConfig {
  webhookUrl: string;
  channel?: string | null;
  enabled?: boolean;
  notifyOnStart?: boolean;
  notifyOnSuccess?: boolean;
  notifyOnFailure?: boolean;
  notifyOnCancelled?: boolean;
}

export interface SlackIntegrationSettings {
  channel?: string | null;
  enabled?: boolean;
  notifyOnStart?: boolean;
  notifyOnSuccess?: boolean;
  notifyOnFailure?: boolean;
  notifyOnCancelled?: boolean;
}

// Repository refs types are now imported from @kilnci/shared

export const useOrganizationsStore = defineStore('organizations', () => {
  const organizations = ref<Organization[]>([]);
  const currentOrganization = ref<Organization | null>(null);
  const integration = ref<GitIntegrationWithCapabilities | null>(null);
  const repositories = ref<Repository[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const authStore = useAuthStore();

  function getHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authStore.token}`,
    };
  }

  // Organizations CRUD

  async function fetchOrganizations(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch('/api/organizations', {
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      organizations.value = data.items;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
    } finally {
      loading.value = false;
    }
  }

  async function fetchOrganizationsFull(): Promise<OrganizationFull[]> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch('/api/organizations?mode=full&pageSize=100', {
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      // Update organizations list with the base organization data
      organizations.value = data.items.map((org: OrganizationFull) => ({
        id: org.id,
        slug: org.slug,
        name: org.name,
        description: org.description,
        createdAt: org.createdAt,
        updatedAt: org.updatedAt,
      }));
      return data.items;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return [];
    } finally {
      loading.value = false;
    }
  }

  async function fetchOrganization(slug: string): Promise<Organization | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}`, {
        headers: getHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Organization not found');
        }
        throw new Error('Failed to fetch organization');
      }

      const data = await response.json();
      currentOrganization.value = data;
      return data;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function createOrganization(data: OrganizationCreate): Promise<Organization | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch('/api/organizations', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to create organization');
      }

      const result = await response.json();
      organizations.value.unshift(result);
      return result;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function updateOrganization(slug: string, data: OrganizationUpdate): Promise<Organization | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to update organization');
      }

      const result = await response.json();
      currentOrganization.value = result;

      // Update in list if present
      const index = organizations.value.findIndex((o) => o.slug === slug);
      if (index !== -1) {
        organizations.value[index] = result;
      }

      return result;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function deleteOrganization(slug: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to delete organization');
      }

      organizations.value = organizations.value.filter((o) => o.slug !== slug);
      if (currentOrganization.value?.slug === slug) {
        currentOrganization.value = null;
      }
      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return false;
    } finally {
      loading.value = false;
    }
  }

  // Git Integration (supports GitLab, Gitea)

  async function fetchIntegration(slug: string): Promise<GitIntegration | null> {
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/integration`, {
        headers: getHeaders(),
      });

      if (response.status === 404) {
        integration.value = null;
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch integration');
      }

      const data = await response.json();
      integration.value = data;
      return data;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    }
  }

  async function configureIntegration(slug: string, data: GitIntegrationConfig): Promise<GitIntegration | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/integration`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to configure integration');
      }

      const result = await response.json();
      integration.value = result;
      return result;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function removeIntegration(slug: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/integration`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to remove integration');
      }

      integration.value = null;
      repositories.value = [];
      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function syncIntegrationRepositories(slug: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/integration/sync`, {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to sync repositories');
      }

      // Refresh repositories after sync
      await fetchRepositories(slug);
      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return false;
    } finally {
      loading.value = false;
    }
  }

  // Repositories

  async function fetchRepositories(slug: string): Promise<Repository[]> {
    error.value = null;

    try {
      // Fetch all pages of repositories
      const allRepos: Repository[] = [];
      let page = 1;
      const pageSize = 100;

      while (true) {
        const response = await fetch(
          `/api/organizations/${slug}/repos?page=${page}&pageSize=${pageSize}`,
          { headers: getHeaders() }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch repositories');
        }

        const data = await response.json();
        allRepos.push(...data.items);

        // Check if we've fetched all pages
        if (data.items.length < pageSize || allRepos.length >= data.total) {
          break;
        }
        page++;
      }

      repositories.value = allRepos;
      return allRepos;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return [];
    }
  }

  async function fetchRepository(slug: string, repoIdOrName: string): Promise<Repository | null> {
    error.value = null;

    try {
      const response = await fetch(
        `/api/organizations/${slug}/repos/${encodeURIComponent(repoIdOrName)}`,
        { headers: getHeaders() }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch repository');
      }

      return await response.json();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    }
  }

  async function fetchAllAccessibleRepositories(): Promise<Repository[]> {
    error.value = null;

    try {
      // First ensure we have the list of organizations
      if (organizations.value.length === 0) {
        await fetchOrganizations();
      }

      // Fetch all repositories from all organizations (with pagination)
      const allRepos: Repository[] = [];
      const pageSize = 100;

      for (const org of organizations.value) {
        let page = 1;
        while (true) {
          const response = await fetch(
            `/api/organizations/${org.slug}/repos?page=${page}&pageSize=${pageSize}`,
            { headers: getHeaders() }
          );

          if (!response.ok) break;

          const data = await response.json();
          allRepos.push(...data.items);

          if (data.items.length < pageSize || allRepos.length >= data.total) {
            break;
          }
          page++;
        }
      }

      return allRepos;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return [];
    }
  }

  async function updateRepository(slug: string, repoId: string, data: RepositoryUpdate): Promise<Repository | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/repos/${repoId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to update repository');
      }

      const result = await response.json();

      // Update in list
      const index = repositories.value.findIndex((r) => r.id === repoId);
      if (index !== -1) {
        repositories.value[index] = result;
      }

      return result;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    } finally {
      loading.value = false;
    }
  }

  function clearCurrent(): void {
    currentOrganization.value = null;
    integration.value = null;
    repositories.value = [];
  }

  // Repository Refs (Branches, Tags, Pull Requests)

  async function fetchBranches(slug: string, repoId: string): Promise<BranchInfo[]> {
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/repos/${repoId}/branches`, {
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to fetch branches');
      }

      return await response.json();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return [];
    }
  }

  async function fetchTags(slug: string, repoId: string): Promise<TagInfo[]> {
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/repos/${repoId}/tags`, {
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to fetch tags');
      }

      return await response.json();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return [];
    }
  }

  async function fetchPullRequests(
    slug: string,
    repoId: string,
    state: 'open' | 'closed' | 'merged' | 'all' = 'open'
  ): Promise<PullRequestInfo[]> {
    error.value = null;

    try {
      const response = await fetch(
        `/api/organizations/${slug}/repos/${repoId}/pull-requests?state=${state}`,
        { headers: getHeaders() }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to fetch pull requests');
      }

      return await response.json();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return [];
    }
  }

  async function fetchPipelines(
    slug: string,
    repoId: string,
    ref?: string,
    refType: UIRefType = 'branch'
  ): Promise<PipelinesAtRefResponse> {
    error.value = null;

    try {
      const params = new URLSearchParams();
      if (ref) params.set('ref', ref);
      params.set('refType', refType);

      const response = await fetch(
        `/api/organizations/${slug}/repos/${repoId}/pipelines?${params}`,
        { headers: getHeaders() }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to fetch pipelines');
      }

      return await response.json();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return { pipelines: [], configExists: false, error: error.value };
    }
  }

  // Slack Integration

  async function fetchSlackIntegration(slug: string, repoId: string): Promise<SlackIntegration | null> {
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/repos/${repoId}/slack`, {
        headers: getHeaders(),
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch Slack integration');
      }

      return await response.json();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    }
  }

  async function configureSlack(slug: string, repoId: string, config: SlackIntegrationConfig): Promise<SlackIntegration | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/repos/${repoId}/slack`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to configure Slack');
      }

      return await response.json();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function updateSlackSettings(slug: string, repoId: string, settings: SlackIntegrationSettings): Promise<SlackIntegration | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/repos/${repoId}/slack`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to update Slack settings');
      }

      return await response.json();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function removeSlackIntegration(slug: string, repoId: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/repos/${repoId}/slack`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to remove Slack integration');
      }

      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function testSlackWebhook(slug: string, repoId: string, webhookUrl: string): Promise<boolean> {
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/repos/${repoId}/slack/test`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ webhookUrl }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to test Slack webhook');
      }

      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return false;
    }
  }

  async function isSlackOAuthAvailable(): Promise<boolean> {
    try {
      const response = await fetch('/api/slack/oauth/available', {
        headers: getHeaders(),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.available === true;
    } catch {
      return false;
    }
  }

  async function getSlackOAuthUrl(slug: string, repoId: string): Promise<string | null> {
    error.value = null;

    try {
      const response = await fetch(`/api/organizations/${slug}/repos/${repoId}/slack/oauth/authorize`, {
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to get Slack OAuth URL');
      }

      const data = await response.json();
      return data.authUrl;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return null;
    }
  }

  async function fetchSchedules(repoName: string): Promise<PipelineScheduleInfo[]> {
    error.value = null;

    try {
      const response = await fetch(`/api/schedules?repo=${encodeURIComponent(repoName)}`, {
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to fetch schedules');
      }

      const data = await response.json();
      return data.items;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return [];
    }
  }

  async function syncSchedules(repositoryId: string): Promise<boolean> {
    error.value = null;

    try {
      const response = await fetch('/api/schedules/sync', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ repositoryId }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to sync schedules');
      }

      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error';
      return false;
    }
  }

  return {
    organizations,
    currentOrganization,
    integration,
    repositories,
    loading,
    error,
    fetchOrganizations,
    fetchOrganizationsFull,
    fetchOrganization,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    fetchIntegration,
    configureIntegration,
    removeIntegration,
    syncIntegrationRepositories,
    fetchRepositories,
    fetchRepository,
    fetchAllAccessibleRepositories,
    updateRepository,
    clearCurrent,
    fetchBranches,
    fetchTags,
    fetchPullRequests,
    fetchPipelines,
    fetchSlackIntegration,
    configureSlack,
    updateSlackSettings,
    removeSlackIntegration,
    testSlackWebhook,
    isSlackOAuthAvailable,
    getSlackOAuthUrl,
    fetchSchedules,
    syncSchedules,
  };
});
